/** #895 — local-native play confirm / timeout verify via exclusive engine, never GetTransportInfo. */

import type { ExclusivePlaybackState } from '../lib/connector';
import type { PlayUriConfirmOutcome, PlayUriPendingSession } from './playUriConfirmation';
import { exclusiveEngineReportsPlaying, isLocalNativeRendererId } from './localNativeRenderer';
import {
  notePlayUriDispatched,
  reanchorAdvanceEpochForAudibleStart,
  setSuppressWallClockAdvance,
} from './positionReconcile';
import {
  hasStartConfirmPositionProgress,
  isStartConfirmGraceActive,
} from './playUriStartConfirmWindow';

export { isLocalNativeRendererId as shouldUseExclusivePlaybackVerify } from './localNativeRenderer';

export async function fetchExclusiveEngineState(): Promise<ExclusivePlaybackState | null> {
  try {
    const { exclusiveGetState } = await import('../lib/connector');
    return await exclusiveGetState();
  } catch {
    return null;
  }
}

/** Read-only — exclusive engine PLAYING/started, not UPnP GetTransportInfo. */
export async function verifyExclusiveEnginePlaying(): Promise<boolean> {
  const state = await fetchExclusiveEngineState();
  return exclusiveEngineReportsPlaying(state);
}

function graceStillStarting(
  session: PlayUriPendingSession,
  hasAcceptanceEvidence: boolean,
): boolean {
  return isStartConfirmGraceActive(session, hasAcceptanceEvidence);
}

/** #895 probe — local-native sessions never call get_transport_info. */
export async function probeLocalNativePlayConfirmation(
  session: PlayUriPendingSession,
  hasAcceptanceEvidence: boolean,
  noteEvidence: (evidence: 'renderer_playing') => void,
): Promise<PlayUriConfirmOutcome> {
  const elapsedMs = Date.now() - session.dispatchedAt;

  if (hasAcceptanceEvidence) {
    if (await verifyExclusiveEnginePlaying()) {
      noteEvidence('renderer_playing');
      return 'success';
    }
    if (graceStillStarting(session, true)) return 'pending';
    if (elapsedMs < session.confirmWindowMs) return 'pending';
    return hasStartConfirmPositionProgress(session) ? 'success' : 'confirmed_failure';
  }

  if (await verifyExclusiveEnginePlaying()) {
    noteEvidence('renderer_playing');
    return 'success';
  }

  if (graceStillStarting(session, false)) return 'pending';
  if (elapsedMs >= session.confirmWindowMs) {
    return hasStartConfirmPositionProgress(session) ? 'success' : 'confirmed_failure';
  }
  return 'pending';
}

/** Apply exclusive-playback-started confirmation for the active local-native renderer (#895). */
export async function confirmLocalNativeExclusivePlaybackStarted(rendererId: string): Promise<boolean> {
  const { useMaestroStore } = await import('../store/maestroStore');
  const store = useMaestroStore.getState();
  const activeId = store.activeConnectorRenderer?.id;
  if (!activeId || rendererId !== activeId) return false;
  if (!isLocalNativeRendererId(rendererId)) return false;

  const { notePlayUriAcceptanceEvidence, clearPlayUriPending } = await import('./playUriConfirmation');

  setSuppressWallClockAdvance(false, 'exclusive-playback-started');
  notePlayUriAcceptanceEvidence('renderer_playing');
  clearPlayUriPending('exclusive_playback_started');
  store.setPlaybackState('playing');
  noteAudiblePlaybackStarted(store.currentTrack?.id);
  return true;
}

/** Re-anchor wall-clock advance on every confirmed audible start (#895). */
export function noteAudiblePlaybackStarted(trackId?: string | null): void {
  notePlayUriDispatched(null);
  reanchorAdvanceEpochForAudibleStart(trackId);
}
